import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import ArrowRightIcon from '@assets/images/arrow-right-icon.svg?react'
import { ProjectFileType, ProjectFolder } from '@types'
import { useProjectPlans } from '@hooks/project/project-plans.hook'
import { useProjectDocuments } from '@hooks/project/project-documents.hook'
import { truncateWithEllipsis } from '@utils/string'
import { Container, BaseTitle, ItemName, Folder, Separator } from './FolderBreadCrumb.styled'
import { useWindowSize } from '@utils/hooks'

interface Props {
  projectId: string
}

export const FolderBreadCrumb = (props: Props): JSX.Element | null => {
  const { projectId } = props
  const { t, i18n } = useTranslation()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { planId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { isMobile } = useWindowSize()
  const folderId = searchParams.get('folderId') as string | undefined

  const { data: plansData } = useProjectPlans(projectId || '')
  const { data: documentsData } = useProjectDocuments(projectId)

  const isPlansPath = pathname.startsWith(`/${i18n.language}/projects/${projectId}/plans`)
  const isDocumentPath = pathname.startsWith(`/${i18n.language}/projects/${projectId}/documents`)
  const isEditorPath = pathname.includes('/editor/')

  const type = isPlansPath ? ProjectFileType.TypePlan : isDocumentPath ? ProjectFileType.TypeDocument : undefined

  if (!type) {
    return null
  }

  const folders = [
    ...((type == ProjectFileType.TypePlan
      ? plansData?.project?.planFolders
      : documentsData?.project?.documentFolders) || []),
  ]

  const currentPlan = isEditorPath && plansData?.project?.plans?.find((plan) => plan.id === planId)
  const currentFolderId = (currentPlan && currentPlan?.folderId) || folderId
  const folder = folders.find((folder) => folder.id == currentFolderId)

  const getBasePath = () => {
    return pathname.split('/editor/')[0]
  }

  const handleFolderClick = (folderId: string, isLastFolder: boolean) => {
    if (isLastFolder && !currentPlan) return
    if (isEditorPath) navigate(getBasePath())
    setSearchParams({
      ...Object.fromEntries([...searchParams]),
      folderId: folderId,
    })
  }

  const selectedFolders: ProjectFolder[] = folder ? [...getParents(folder, folders), folder] : []

  const renderFolders = () => {
    if (isMobile && selectedFolders.length > 3) {
      return (
        <>
          <Folder key={selectedFolders[0].id}>
            <Separator>
              <ArrowRightIcon />
            </Separator>
            <ItemName isLast={false} onClick={() => handleFolderClick(selectedFolders[0].id, false)}>
              {selectedFolders[0]?.name && truncateWithEllipsis(selectedFolders[0].name, 15)}
            </ItemName>
          </Folder>

          <Separator>
            <ArrowRightIcon />
          </Separator>
          <ItemName isLast={false}>...</ItemName>

          <Folder key={selectedFolders[selectedFolders.length - 1].id}>
            <Separator>
              <ArrowRightIcon />
            </Separator>
            <ItemName
              isLast={!currentPlan}
              onClick={() => handleFolderClick(selectedFolders[selectedFolders.length - 1].id, true)}
            >
              {selectedFolders[selectedFolders.length - 1]?.name &&
                truncateWithEllipsis(selectedFolders[selectedFolders.length - 1].name, isMobile ? 15 : 20)}
            </ItemName>
          </Folder>
        </>
      )
    }

    return selectedFolders.map((folder, index) => (
      <Folder key={folder.id}>
        <Separator>
          <ArrowRightIcon />
        </Separator>
        <ItemName
          isLast={!currentPlan && index === selectedFolders.length - 1}
          onClick={() => handleFolderClick(folder.id, index === selectedFolders.length - 1)}
        >
          {folder?.name && truncateWithEllipsis(folder.name, isMobile ? 15 : 20)}
        </ItemName>
      </Folder>
    ))
  }

  return (
    <Container>
      <BaseTitle onClick={() => navigate(getBasePath())}>
        {type == ProjectFileType.TypePlan
          ? t('modules_shared_components_FolderPicker_plan_label')
          : t('modules_shared_components_FolderPicker_document_label')}
      </BaseTitle>

      {renderFolders()}

      {currentPlan && (
        <>
          <Separator>
            <ArrowRightIcon />
          </Separator>
          <ItemName isLast={true}>{truncateWithEllipsis(currentPlan.name, isMobile ? 25 : 100)}</ItemName>
        </>
      )}
    </Container>
  )
}

const getParent = (child: ProjectFolder, folders: ProjectFolder[]) => {
  return folders.find((folder) => folder.id == child.parentId)
}

const getParents = (folder: ProjectFolder, folders: ProjectFolder[]): ProjectFolder[] => {
  const parent = getParent(folder, folders)
  if (parent) {
    return [...getParents(parent, folders), parent]
  }
  return []
}
